import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const warehouseManagerApi = createApi({
  reducerPath: "warehouseManager",
  tagTypes: ["warehouseManager", "warehouseManagerOrders"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getDeviceStatuses: builder.query<{ data: ApiTypes.Res.DeviceStatusesWidget }, void>({
      query: () => {
        return {
          url: `/warehouse-manager/widgets/device-statuses`,
          method: "GET",
        }
      },
    }),
    getWHDevices: builder.query<ApiTypes.Res.WHManagerDevices, UITypes.Params>({
      query: arg => {
        return {
          url: `/warehouse-manager/devices`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    getWHODevicesWidget: builder.query<{ data: ApiTypes.Res.WHManagerDevicesWidget }, void>({
      query: () => {
        return {
          url: `/warehouse-manager/widgets/device-in-warehouse`,
        }
      },
    }),
    updateWHDevicesStatus: builder.query<void, UITypes.Params>({
      query: arg => {
        return {
          url: `/warehouse-manager/devices/status`,
          method: "PUT",
          params: prepareQueryParams(arg),
        }
      },
    }),
    validateDevicesBulk: builder.query<{ data: ApiTypes.Res.DeviceValidattion }, FormData>({
      query: body => {
        return {
          url: `/warehouse-manager/validate-devices-bulk`,
          method: "POST",
          body,
        }
      },
    }),
    addDevicesBulk: builder.query<{ data: ApiTypes.Res.DeviceValidattion }, ApiTypes.Req.AddDevices>({
      query: body => {
        return {
          url: `/warehouse-manager/add-devices-bulk`,
          method: "POST",
          body,
          headers: { Accept: "application/json" },
        }
      },
    }),
    getWHManagerInfo: builder.query<{ data: ApiTypes.Res.WHManagerInfo }, void>({
      query: body => {
        return {
          url: `/warehouse-manager/info`,
          body,
        }
      },
    }),
    // New Orders API section
    getWHOrdersNew: builder.query<ApiTypes.Res.WHManagerOrdersNew, UITypes.Params>({
      query: arg => {
        return {
          url: `/warehouse-manager/orders2`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
      providesTags: ["warehouseManagerOrders"],
    }),
    getWHManagerOrderDevices: builder.query<{ data: string[] }, number>({
      query: id => ({
        url: `/warehouse-manager/orders2/item/${id}/devices`,
        method: "GET",
      }),
    }),
    getWHManagerOrder: builder.query<ApiTypes.Model.WHManagerOrderNew, number>({
      query: id => ({
        url: `/warehouse-manager/orders2/${id}`,
        method: "GET",
      }),
    }),
    validateWHDeviceForPacking: builder.query<{ value: boolean }, string>({
      query: sn => ({
        url: `/warehouse-manager/orders2/validate-device-for-packing/${sn}`,
        method: "GET",
      }),
    }),
    packWHManagerOrderItem: builder.mutation<void, { id: number; serialNumbers: string[] }>({
      query: data => ({
        url: `/warehouse-manager/orders2/item/${data.id}/packing`,
        method: "POST",
        body: { serial_numbers: data.serialNumbers },
      }),
      invalidatesTags: ["warehouseManagerOrders"],
    }),
    replaceWHManagerOrderItem: builder.mutation<void, { id: number; oldSN: string; newSN: string }>({
      query: data => ({
        url: `/warehouse-manager/orders2/item/${data.id}/replace`,
        method: "POST",
        body: { old_sn: data.oldSN, new_sn: data.newSN },
      }),
    }),
    shipWHManagerOrder: builder.mutation<void, number>({
      query: id => ({
        url: `/warehouse-manager/orders2/item/${id}/ship`,
        method: "POST",
      }),
      invalidatesTags: ["warehouseManagerOrders"],
    }),
    addWHManagerOrderTrackingNumbers: builder.mutation<void, { id: number; trackingNumbers: string[] }>({
      query: data => ({
        url: `/warehouse-manager/orders2/item/${data.id}/trackings`,
        method: "POST",
        body: { tracking_numbers: data.trackingNumbers },
      }),
      invalidatesTags: ["warehouseManagerOrders"],
    }),
    getOrderStatusesNew: builder.query<{ data: ApiTypes.Res.OrderStatusesWidget }, void>({
      query: () => {
        return {
          url: `/warehouse-manager/widgets/order2-statuses`,
          method: "GET",
        }
      },
    }),
  }),
})

export const {
  useLazyGetDeviceStatusesQuery,
  useLazyGetWHDevicesQuery,
  useLazyGetWHODevicesWidgetQuery,
  useLazyUpdateWHDevicesStatusQuery,
  useLazyValidateDevicesBulkQuery,
  useLazyGetWHManagerInfoQuery,
  useLazyAddDevicesBulkQuery,
  useLazyGetWHOrdersNewQuery,
  useLazyGetWHManagerOrderDevicesQuery,
  useLazyValidateWHDeviceForPackingQuery,
  usePackWHManagerOrderItemMutation,
  useReplaceWHManagerOrderItemMutation,
  useShipWHManagerOrderMutation,
  useAddWHManagerOrderTrackingNumbersMutation,
  useLazyGetOrderStatusesNewQuery,
} = warehouseManagerApi
